import { Loader2, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAnalyticsAuth } from '@/lib/useAnalyticsAuth';
import { LoginPage } from '@/pages/loginPage';

type AuthMessageProperties = {
  action?: () => void;
  actionLabel?: string;
  message: string;
  title: string;
};

const AuthMessage = ({ action, actionLabel, message, title }: AuthMessageProperties) => (
  <div className="bg-canvas grid min-h-screen place-items-center p-4">
    <Card className="w-full max-w-md">
      <CardHeader className="flex flex-row items-center gap-3">
        <ShieldAlert aria-hidden="true" className="text-muted size-5" />
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-muted text-sm">{message}</p>
        {action && actionLabel ? (
          <Button onClick={action} variant="primary">
            {actionLabel}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  </div>
);

const AuthLoadingOverlay = () => (
  <div className="bg-canvas/20 fixed inset-0 z-50 grid place-items-center backdrop-blur-[2px]" data-auth-loading="true">
    <div
      aria-live="polite"
      className="border-line bg-raised/90 shadow-raised grid size-12 place-items-center rounded-full border"
      role="status"
    >
      <Loader2 aria-hidden="true" className="text-accent size-5 animate-spin" />
      <span className="sr-only">Checking analytics access</span>
    </div>
  </div>
);

export const AuthGate = ({ children }: { children: ReactNode }) => {
  const { error, logout, reload, status } = useAnalyticsAuth();
  const sessionLoading = status === 'loading';

  if (sessionLoading || status === 'authenticated') {
    return (
      <div className="relative min-h-screen">
        <div
          aria-hidden={sessionLoading || undefined}
          className={sessionLoading ? 'pointer-events-none min-h-screen blur-[2px] select-none' : undefined}
          inert={sessionLoading || undefined}
        >
          {children}
        </div>
        {sessionLoading ? <AuthLoadingOverlay /> : null}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <AuthMessage
        action={() => void reload()}
        actionLabel="Retry"
        message={error ?? 'Unable to load analytics access.'}
        title="Session error"
      />
    );
  }

  if (status === 'unauthenticated' || status === 'newPasswordRequired') {
    return <LoginPage />;
  }

  if (status === 'unauthorized') {
    return (
      <AuthMessage
        action={() => void logout()}
        actionLabel="Sign out"
        message="Your account is authenticated but is not in the InternalAnalyticsOperators Cognito group."
        title="Analytics access required"
      />
    );
  }
  return null;
};
